// AP-PERF A5 — background video starter.
// snippets/video-util.liquid renders autoplay videos as <ap-autoplay><video preload="none" data-ap-autoplay>,
// so nothing is downloaded while the page itself is loading. One screen before a video is reached, its small
// placeholder poster is swapped for the sharp one (data-ap-poster). After the window load event, each video
// starts once it is within 200px of the viewport. With prefers-reduced-motion or Save-Data the video never
// starts and the poster stays. A custom element (not an inline script), so content swapped in by Barba works too.
(() => {
  if (customElements.get('ap-autoplay')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  const pageLoaded = new Promise((resolve) => {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', () => resolve(), { once: true });
  });
  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              entry.target.closest('ap-autoplay')?.start();
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const posterObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              posterObserver.unobserve(entry.target);
              if (entry.target.dataset.apPoster) entry.target.poster = entry.target.dataset.apPoster;
            });
          },
          { rootMargin: '100% 0px' }
        )
      : null;

  class ApAutoplay extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video[data-ap-autoplay]');
      if (!this.video) return;
      if (this.video.dataset.apPoster && posterObserver) posterObserver.observe(this.video);
      if (reducedMotion.matches || saveData()) return;
      pageLoaded.then(() => {
        if (!this.isConnected) return;
        if (observer) observer.observe(this.video);
        else this.start();
      });
    }

    disconnectedCallback() {
      if (observer && this.video) observer.unobserve(this.video);
      if (posterObserver && this.video) posterObserver.unobserve(this.video);
    }

    start() {
      const video = this.video;
      if (!video || video.dataset.apStarted) return;
      video.dataset.apStarted = 'true';
      // video-util (theme bundle) plays/pauses videos that carry the autoplay attribute as they enter/leave the viewport.
      video.setAttribute('autoplay', '');
      video.preload = 'auto';
      this.tryPlay();
    }

    // Where media playback needs a user action (WKWebView defaults, used by some in-app browsers; Low Power Mode),
    // play() is refused with NotAllowedError, as are the theme's own play() calls. Then try once more on the
    // visitor's first tap or key press, if the video is still near the screen. Otherwise the poster stays.
    tryPlay() {
      const video = this.video;
      const playing = video.play();
      if (!playing) return;
      playing.catch((error) => {
        if (!error || error.name !== 'NotAllowedError' || !this.isConnected) return;
        const events = ['pointerdown', 'touchstart', 'keydown'];
        const once = () => {
          events.forEach((name) => window.removeEventListener(name, once, true));
          if (!video.paused || !this.isConnected) return;
          const box = video.getBoundingClientRect();
          if (box.bottom < -200 || box.top > window.innerHeight + 200) return;
          video.play().catch(() => {});
        };
        events.forEach((name) => window.addEventListener(name, once, { capture: true, passive: true }));
      });
    }
  }

  customElements.define('ap-autoplay', ApAutoplay);

  // AP-PERF A7 — deferred images (responsive-image `defer: true`, homepage sliders below the hero).
  // After the load event, each image gets its real srcset/src once it is within one screen of the viewport.
  const swapIn = (img) => {
    if (img.dataset.apSrcset) img.srcset = img.dataset.apSrcset;
    if (img.dataset.apSrc) img.src = img.dataset.apSrc;
    img.removeAttribute('data-ap-srcset');
    img.removeAttribute('data-ap-src');
  };
  pageLoaded.then(() => {
    const images = document.querySelectorAll('img[data-ap-srcset], img[data-ap-src]');
    if (!('IntersectionObserver' in window)) return images.forEach(swapIn);
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          imageObserver.unobserve(entry.target);
          swapIn(entry.target);
        });
      },
      { rootMargin: '100% 0px' }
    );
    images.forEach((img) => imageObserver.observe(img));
  });
})();
